// Shared column/field configurations for Project Objective Create & Details

import { PO_PICKLIST_LABELS } from "../../config/projectObjectiveFields";

// Details page builders (use nested paths)
export function buildInfoFieldsForDetails({
  isEditing = false,
  form = null,
  data = null,
} = {}) {
  return [
    {
      label: "Project Objective Name",
      path: "info.name",
      type: "text",
      readOnly: true,
    },
    {
      label: "Contributor Facing Name",
      path: "info.contributorFacingName",
      type: "text",
      readOnly: true,
    },
    {
      label: "Project Objective Description",
      path: "info.objectiveDescription",
      type: "text",
    },
    {
      label: "Append to Objective Name",
      path: "info.appendToObjectiveName",
      type: "text",
    },
    { label: "Start Date", path: "info.dateStart", type: "date" },
    { label: "End Date", path: "info.dateEnd", type: "date" },
    {
      label: "Total Target Productivity Hours",
      path: "info.totalTargetProductivityHours",
      type: "number",
      readOnly: true,
    },
    {
      label: "PO ID for Reports",
      path: "info.poIdForReports",
      type: "text",
      readOnly: true,
    },
    {
      label: "Project",
      path: "info.projectId",
      type: "lookup",
      lookupObject: "Project",
      value: (form || data)?.info?.projectName || "—",
      linkTo:
        !isEditing && (form || data)?.info?.projectId
          ? `/p/${(form || data)?.info?.projectId}`
          : undefined,
      valueLabel: (form || data)?.info?.projectName || "—",
    },
  ];
}

export function buildRequirementsFieldsForDetails({ form, data } = {}) {
  const source = form || data || {};
  const country = source?.requirements?.country || "";
  const isUnitedStates = country === "United States";
  const areStatesExcluded = source?.requirements?.excludeStates === true;
  const isCountrySelected =
    country !== undefined && country !== "" && country !== null;
  return [
    {
      label: "Country",
      path: "requirements.country",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.COUNTRY,
    },
    !isCountrySelected && {
      label: "Include in Country List",
      path: "requirements.includeInCountryList",
      type: "checkbox",
    },
    !isCountrySelected && {
      label: "List of Countries",
      path: "requirements.listOfCountries",
      type: "multi-select",
      picklistLabel: PO_PICKLIST_LABELS.COUNTRY,
    },
    isUnitedStates && {
      label: "Exclude States",
      path: "requirements.excludeStates",
      type: "checkbox",
    },
    isUnitedStates &&
      areStatesExcluded && {
        label: "Currently Residing US State",
        path: "requirements.currentlyResidingUsState",
        type: "multi-select",
        picklistLabel: PO_PICKLIST_LABELS.CURRENTLY_RESISDING_US_STATE,
        required: true,
      },
    {
      label: "Language",
      path: "requirements.language",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.LANGUAGE,
    },
    {
      label: "Dialect Country",
      path: "requirements.dialectCountry",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.DIALECT,
    },
    {
      label: "Language Skill Level",
      path: "requirements.languageSkillLevel",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.LANGUAGE_SKILL_LEVEL,
    },
    {
      label: "Fluency Type",
      path: "requirements.fluencyType",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.FLUENCY_TYPE,
    },
    {
      label: "Degree Requirement",
      path: "requirements.degreeRequirement",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.DEGREE_REQUIREMENT,
    },
    {
      label: "Additional Manual Match Required",
      path: "requirements.additionalManualMatchRequired",
      type: "checkbox",
    },
    {
      label: "Cap Daily Hours Submitted",
      path: "requirements.capDailyHoursSubmitted",
      type: "checkbox",
    },
    {
      label: "Work Type",
      path: "requirements.workType",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.WORK_TYPE,
    },
  ].filter(Boolean);
}

// Details sections: editable fields
export function buildFunnelFieldsForDetails() {
  return [
    { label: "Funnel A", path: "funnelSplits.funnelA", type: "number" },
    { label: "Funnel B", path: "funnelSplits.funnelB", type: "number" },
    { label: "Funnel C", path: "funnelSplits.funnelC", type: "number" },
    { label: "Funnel D", path: "funnelSplits.funnelD", type: "number" },
    { label: "Funnel E", path: "funnelSplits.funnelE", type: "number" },
  ];
}

export function buildDiversityFieldsForDetails() {
  return [
    {
      label: "Minimum Contributors",
      path: "diversity.minimumContributors",
      type: "number",
    },
  ];
}

export function buildTargetsFieldsForDetails({ form, data }) {
  const isTargetTypeUnits =
    (form || data)?.targets?.productivityTargetType === "Units & units/hour";
  return [
    {
      label: "Productivity Target Type",
      path: "targets.productivityTargetType",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.PRODUCTIVITY_TARGET_TYPE,
    },
    {
      label: "Weekly Target Production Hours",
      path: "targets.weeklyTargetProductionHours",
      type: "text",
      readOnly: true,
    },
    {
      label: "Weekly Target Production Units",
      path: "targets.weeklyTargetProductionUnits",
      type: "text",
    },
    { label: "Unit per Hour", path: "targets.unitPerHour", type: "text" },
    {
      label: "Target Production Units",
      path: "targets.targetProductionUnits",
      type: "text",
    },
    {
      label: "Target Production Hours",
      path: "targets.targetProductionHours",
      type: "text",
    },
    isTargetTypeUnits && {
      label: "Production Payment Threshold",
      path: "targets.productionPaymentThreshold",
      type: "number",
      required: true,
    },
    isTargetTypeUnits && {
      label: "Quality Payment Threshold",
      path: "targets.qualityPaymentThreshold",
      type: "number",
      required: true,
    },
  ].filter(Boolean);
}

export function buildStatsFieldsForDetails(stats = {}) {
  return [
    {
      label: "Invited Contributors",
      value: stats.invitedAvailableContributors ?? "—",
    },
    { label: "Applications Received", value: stats.appReceived ?? "—" },
    { label: "Matched Contributors", value: stats.matchedContributors ?? "—" },
    {
      label: "Qualified Contributors",
      value: stats.qualifiedContributors ?? "—",
    },
    { label: "Active Contributors", value: stats.activeContributors ?? "—" },
    {
      label: "Production Contributors",
      value: stats.productionContributors ?? "—",
    },
    { label: "Removed", value: stats.removed ?? "—" },
  ];
}

export function buildRatesFieldsForDetails() {
  return [
    {
      label: "Non-US Fairpay Rate",
      path: "rates.nonUSFairpayRate",
      type: "text",
    },
    {
      label: "Non-US Project Pay Rate",
      path: "rates.nonUSProjectPayRate",
      type: "text",
    },
    {
      label: "Project Objective Pay Rate",
      path: "rates.projectObjectivePayRate",
      type: "text",
    },
    {
      label: "Project Rate",
      path: "rates.projectRate",
      type: "text",
      readOnly: true,
    },
    {
      label: "Project Pay Rate",
      path: "rates.projectPayRate",
      type: "text",
    },
    { label: "Client Pay Rate", path: "rates.clientPayRate", type: "text" },
    { label: "Hide Pay Rate", path: "rates.hidePayRate", type: "checkbox" },
    {
      label: "Minimum Rate",
      path: "rates.minimumRate",
      type: "text",
      info: "This is the minimum rate that will appear in the Lever posting. This is NOT used in the calculation of contributor pay rates.",
    },
    {
      label: "Maximum Rate",
      path: "rates.maximumRate",
      type: "text",
      info: "This is the maximum rate that will appear in the Lever posting. This is NOT used in the calculation of contributor pay rates.",
    },
  ];
}

export function buildSysFieldsForDetails(sys = {}) {
  return [
    { label: "CreatedById", value: sys.createdById || "—" },
    { label: "LastModifiedById", value: sys.lastModifiedById || "—" },
  ];
}

// Create modal builders (use flat paths to match initialFormState)
export function buildInfoFieldsForCreate() {
  return [
    {
      label: "Project",
      path: "project",
      type: "searchable",
      required: true,
      searchType: "project",
    },
    {
      label: "Append to Objective Name",
      path: "appendToObjectiveName",
      type: "text",
    },
    { label: "Date Start", path: "dateStart", type: "date" },
    { label: "Date End", path: "dateEnd", type: "date" },
  ];
}

export function buildRequirementsFieldsForCreate({ form } = {}) {
  const country = form?.country;
  const isCountrySelected =
    country !== undefined && country !== "" && country !== null;
  const isUnitedStates = form?.country === "United States";
  const areStatesExcluded = form?.excludeStates === true;

  return [
    {
      label: "Country",
      path: "country",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.COUNTRY,
      info: "Either country or language must be selected",
    },
    isUnitedStates && {
      label: "Exclude US States",
      path: "excludeStates",
      type: "checkbox",
    },
    isUnitedStates &&
      areStatesExcluded && {
        label: "Currently Residing US State",
        path: "currentlyResidingUsState",
        type: "multi-select",
        picklistLabel: PO_PICKLIST_LABELS.CURRENTLY_RESISDING_US_STATE,
        required: true,
      },
    !isCountrySelected && {
      label: "Include in Country List",
      path: "includeInCountryList",
      type: "checkbox",
    },
    !isCountrySelected && {
      label: "List of Countries",
      path: "listOfCountries",
      type: "multi-select",
      picklistLabel: PO_PICKLIST_LABELS.COUNTRY,
    },
    {
      label: "Work Type",
      path: "workType",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.WORK_TYPE,
      required: true,
    },
    {
      label: "Additional Manual Match Required",
      path: "additionalManualMatchRequired",
      type: "checkbox",
    },
    {
      label: "Cap Daily Hours Submitted",
      path: "capDailyHoursSubmitted",
      type: "checkbox",
    },
    {
      label: "Language",
      path: "language",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.LANGUAGE,
    },
    {
      label: "Dialect Country",
      path: "dialect",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.DIALECT,
    },
    {
      label: "Language Skill Level",
      path: "languageSkillLevel",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.LANGUAGE_SKILL_LEVEL,
    },
    {
      label: "Fluency Type",
      path: "fluencyType",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.FLUENCY_TYPE,
    },
    {
      label: "Degree Requirement",
      path: "degreeRequirement",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.DEGREE_REQUIREMENT,
    },
  ].filter(Boolean);
}

export function buildProductivityFieldsForCreate(form) {
  const isTargetTypeUnits =
    form?.productivityTargetType === "Units & units/hour";
  return [
    {
      label: "Productivity Target Type",
      path: "productivityTargetType",
      type: "select",
      picklistLabel: PO_PICKLIST_LABELS.PRODUCTIVITY_TARGET_TYPE,
      required: true,
    },
    {
      cols: 2,
      fields: [
        {
          label: "Target Production Hours",
          path: "targetProductionHours",
          type: "number",
        },
        { label: "Unit Pay Rate", path: "unitPayRate", type: "number" },
      ],
    },
    { label: "Main Quality Metric", path: "mainQualityMetric", type: "text" },
    {
      cols: 2,
      fields: [
        {
          label: "Contributor Quality Flag",
          path: "contributorQualityFlag",
          type: "text",
        },
        {
          label: "Contributor Productivity Flag",
          path: "contributorProductivityFlag",
          type: "text",
        },
      ],
    },
    {
      label: "Contributor Overproductivity Flag",
      path: "contributorOverproductivityFlag",
      type: "text",
    },
    isTargetTypeUnits && {
      cols: 2,
      fields: [
        {
          label: "Production Payment Threshold",
          path: "productionPaymentThreshold",
          type: "number",
          required: isTargetTypeUnits,
        },
        {
          label: "Quality Payment Threshold",
          path: "qualityPaymentThreshold",
          type: "number",
          required: isTargetTypeUnits,
        },
      ],
    },
  ].filter(Boolean);
}

export function buildFunnelFieldsForCreate() {
  return [
    {
      cols: 2,
      fields: [
        { label: "Funnel A (%)", path: "funnelAPercentage", type: "number" },
        { label: "Funnel B (%)", path: "funnelBPercentage", type: "number" },
      ],
    },
    {
      cols: 2,
      fields: [
        { label: "Funnel C (%)", path: "funnelCPercentage", type: "number" },
        { label: "Funnel D (%)", path: "funnelDPercentage", type: "number" },
      ],
    },
    { label: "Funnel E (%)", path: "funnelEPercentage", type: "number" },
  ];
}

export function buildRatesFieldsForCreate() {
  return [
    {
      cols: 2,
      fields: [
        { label: "Minimum Rate", path: "minimumRate", type: "number" },
        { label: "Maximum Rate", path: "maximumRate", type: "number" },
      ],
    },
    {
      cols: 2,
      fields: [
        { label: "Pay Interval", path: "payInterval", type: "text" },
        { label: "Hide Payrate", path: "hidePayRate", type: "text" },
      ],
    },
    {
      cols: 2,
      fields: [
        { label: "Unit Pay Rate", path: "unitPayRate", type: "number" },
        {
          label: "Project Objective Pay Rate",
          path: "projectObjectivePayRate",
          type: "number",
        },
      ],
    },
  ];
}

export function buildActionRulesFieldsForCreate() {
  return [
    {
      cols: 2,
      fields: [
        {
          label: "Days Between Reminder Emails",
          path: "daysBetweenReminderEmails",
          type: "number",
        },
        {
          label: "Email Content Complete",
          path: "emailContentComplete",
          type: "checkbox",
        },
      ],
    },
  ];
}

export function buildSystemFieldsForCreate() {
  return [
    {
      cols: 2,
      fields: [
        {
          label: "Created By",
          path: "createdBy",
          type: "text",
          readOnly: true,
          info: "Calculated upon save",
        },
        {
          label: "Last Modified By",
          path: "lastModifiedBy",
          type: "text",
          readOnly: true,
          info: "Calculated upon save",
        },
      ],
    },
  ];
}
